import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { logError } from '../utils/log';

const PlayerStatsModal = ({ player, series, onClose }) => {
    const { mysteryOutHits, mysteryOutPayout } = useMemo(() => {
        let hits = 0;
        let payout = 0;
        if (series?.mysteryOut && player?.name) {
            const playerWins = series.mysteryOut.filter(
                entry => entry && typeof entry.player === 'string' && entry.player.toLowerCase() === player.name.toLowerCase() && entry.player.toLowerCase() !== 'x'
            );
            hits = playerWins.length;
            payout = playerWins.reduce((sum, entry) => sum + (entry?.total ?? 0), 0);
        }
        return { mysteryOutHits: hits, mysteryOutPayout: payout };
    }, [series?.mysteryOut, player?.name]);

    const tournamentWinsCount = useMemo(() => {
        if (!player?.entries) return 0;
        return player.entries.filter(entry => entry && entry.points === 7).length;
    }, [player?.entries]);

    const runnerUpsCount = useMemo(() => {
        if (!player?.entries) return 0;
        return player.entries.filter(entry => entry && entry.points === 6).length;
    }, [player?.entries]);

    const tournamentWinningsTotal = useMemo(() => {
        let total = 0;
        if (!player?.entries || !series?.weekInfo) return 0;
        const weekInfoMap = new Map(series.weekInfo.map(info => [info.week, info]));
        player.entries.forEach(entry => {
            if (entry && entry.week) {
                const weekInfo = weekInfoMap.get(entry.week);
                if (weekInfo) {
                    if (entry.points === 7) total += (weekInfo.firstPlacePayout ?? 0);
                    else if (entry.points === 6) total += (weekInfo.secondPlacePayout ?? 0);
                    else if (entry.points === 5) total += (weekInfo.thirdPlacePayout ?? 0);
                }
            }
        });
        return total;
    }, [player?.entries, series?.weekInfo]);

    const totalCash = useMemo(() => {
        return mysteryOutPayout + tournamentWinningsTotal;
    }, [mysteryOutPayout, tournamentWinningsTotal]);

    const sortedEntries = useMemo(() => {
        if (!player?.entries) return [];
        return [...player.entries].sort((a, b) => (a.week ?? 0) - (b.week ?? 0));
    }, [player?.entries]);

    const isHighestOutPlayer = useMemo(() => {
        const overallHighest = series?.summary?.totals?.overallHighestOut || 0;
        return player.highestOut === overallHighest && player.highestOut > 0;
    }, [player.highestOut, series?.summary?.totals?.overallHighestOut]);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="card rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] flex flex-col border border-primary-two" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                    <div className="flex flex-col gap-1">
                        <h3 className="text-xl font-bold heading-primary-1">{player.name}</h3>
                        <div className="flex flex-wrap gap-1">
                            {player.isFinalist && <Tag label="Finalist" role="success" />}
                            {player.hasRaffle6Plus && <Tag label="6+" role="info" />}
                            {player.hasRaffle8Plus && <Tag label="8+" role="warning" />}
                            {player.hasRaffle10Plus && <Tag label="10+" role="warning" />}
                            {isHighestOutPlayer && <Tag label="High Out" role="primary" />}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                </div>
                <div className="p-4 overflow-y-auto space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                        <StatTile label="Total Points" value={player.totalPoints} />
                        <StatTile label="Total Weeks" value={player.totalWeeks} />
                        <StatTile label="Highest Out" value={player.highestOut || 'x'} />
                        <StatTile label="Rank" value={player.rank} />
                        {tournamentWinsCount > 0 && <StatTile label="Tourney Wins" value={tournamentWinsCount} role="success" />}
                        {runnerUpsCount > 0 && <StatTile label="Runner Ups" value={runnerUpsCount} role="info" />}
                        {mysteryOutHits > 0 && <StatTile label="Mystery Outs" value={mysteryOutHits} role="warning" />}
                        {mysteryOutPayout > 0 && <StatTile label="Mystery Payout" value={`$${mysteryOutPayout.toFixed(2)}`} role="warning" />}
                        {tournamentWinningsTotal > 0 && <StatTile label="Tourney Winnings" value={`$${tournamentWinningsTotal.toFixed(2)}`} role="success" />}
                        {totalCash > 0 && <StatTile label="Total Cash" value={`$${totalCash.toFixed(2)}`} role="success" />}
                    </div>
                    {sortedEntries.length > 0 ? (
                        <div className="overflow-x-auto rounded-lg border border-low">
                            <table className="min-w-full text-sm">
                                <thead className="table-header">
                                    <tr>
                                        <th className="px-3 py-2 text-center font-semibold">Week</th>
                                        <th className="px-3 py-2 text-center font-semibold">Points</th>
                                        <th className="px-3 py-2 text-center font-semibold">High Out</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedEntries.map((entry, idx) => (
                                        <tr key={entry.id || idx} className="row-alt">
                                            <td className="px-3 py-2 text-center">{entry.week}</td>
                                            <td className="px-3 py-2 text-center">{entry.isDNP ? '2 (DNP)' : entry.points}</td>
                                            <td className="px-3 py-2 text-center">{entry.highOut || 'x'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-center text-muted italic">No weekly entries found.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatTile = ({ label, value, role }) => (
    <div className={`p-3 rounded-lg text-center ${role ? `bg-${role}-soft` : 'surface-low'}`}>
        <div className="text-sm font-semibold text-muted">{label}</div>
        <div className="text-lg font-bold text-strong">{value}</div>
    </div>
);

const Tag = ({ label, role }) => {
    const roleClass = `tag-${role}`;
    return <span className={`tag ${roleClass} text-[0.6rem] font-semibold`}>{label}</span>;
};

export const LeaderboardPage = () => {
    const [allSeasons, setAllSeasons] = useState([]);
    const [selectedSeason, setSelectedSeason] = useState('');
    const [series, setSeries] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPlayer, setSelectedPlayer] = useState(null);

    // Fetch all available seasons (years) to populate the dropdown
    useEffect(() => {
        const fetchSeasons = async () => {
            try {
                const snap = await getDocs(collection(db, 'pointSeries'));
                const seasons = snap.docs.map(d => {
                    const data = d.data();
                    return { id: d.id, name: data.name || 'Unnamed Series', year: data.year || 'N/A' };
                }).sort((a, b) => {
                    if (b.year !== a.year) return (b.year || '').localeCompare(a.year || '');
                    return (a.name || '').localeCompare(b.name || '');
                });
                setAllSeasons(seasons);
                if (seasons.length > 0 && seasons[0].id) {
                    setSelectedSeason(seasons[0].id); // Default to the most recent season's ID
                } else {
                    setLoading(false); // No seasons, stop loading
                }
            } catch (e) {
                logError('Error fetching seasons:', e);
                setLoading(false);
            }
        };
        fetchSeasons();
    }, []);

    // Fetch the data for the selected season
    useEffect(() => {
        if (!selectedSeason) return;

        const load = async () => {
            setLoading(true);
            try {
                const seriesDoc = await getDoc(doc(db, 'pointSeries', selectedSeason));
                if (seriesDoc.exists()) {
                    setSeries({ id: seriesDoc.id, ...seriesDoc.data() });
                } else {
                    setSeries(null);
                }
            } catch (e) {
                logError('Error loading point series:', e);
                setSeries(null);
            }
            setLoading(false);
        };
        load();
    }, [selectedSeason]);

    const players = useMemo(() => {
        const list = Array.isArray(series?.players) ? series.players.filter(Boolean) : [];
        const sorted = list.slice().sort((a, b) => {
            if ((b.totalPoints || 0) !== (a.totalPoints || 0)) return (b.totalPoints || 0) - (a.totalPoints || 0);
            if ((b.highestOut || 0) !== (a.highestOut || 0)) return (b.highestOut || 0) - (a.highestOut || 0);
            if ((b.totalWeeks || 0) !== (a.totalWeeks || 0)) return (b.totalWeeks || 0) - (a.totalWeeks || 0);
            return (a.name || '').localeCompare(b.name || '');
        });

        let currentRank = 1;
        let prevPoints = null;
        let prevHighOut = null;
        let prevWeeks = null;

        return sorted.map((player, index) => {
            if (!player) return null;
            const playerPoints = player.totalPoints || 0;
            const playerHighOut = player.highestOut || 0;
            const playerWeeks = player.totalWeeks || 0;

            if (playerPoints !== prevPoints || playerHighOut !== prevHighOut || playerWeeks !== prevWeeks) {
                currentRank = index + 1;
            }
            prevPoints = playerPoints;
            prevHighOut = playerHighOut;
            prevWeeks = playerWeeks;
            return { ...player, rank: currentRank };
        }).filter(Boolean);
    }, [series]);

    const { mysteryOutWins, mysteryOutTotalPayout, currentPotDisplay } = useMemo(() => {
        if (!series?.mysteryOut) return { mysteryOutWins: 0, mysteryOutTotalPayout: 0, currentPotDisplay: 0 };

        let winsCount = 0;
        let totalPayoutAmount = 0;
        let currentPotAccumulated = 0;

        const sortedEntries = [...series.mysteryOut].sort((a, b) => (a.week || 0) - (b.week || 0));

        sortedEntries.forEach(entry => {
            if (!entry || typeof entry.player !== 'string') return;

            if (entry.player.toLowerCase() === 'x') {
                currentPotAccumulated += (entry.total ?? 0);
            } else {
                winsCount++;
                totalPayoutAmount += (entry.total ?? 0);
                currentPotAccumulated = 0; // Pot resets after a win
            }
        });
        return {
            mysteryOutWins: winsCount,
            mysteryOutTotalPayout: totalPayoutAmount,
            currentPotDisplay: currentPotAccumulated
        };
    }, [series?.mysteryOut]);

    if (loading) return <div className="text-center">Loading leaderboard...</div>;
    
    if (!series) return (
        <div className="space-y-4">
            <h2 className="text-4xl font-extrabold text-center text-indigo-700 dark:text-indigo-300 mb-6">Point Series Leaderboard</h2>
            <p className="text-center text-gray-600 dark:text-gray-300">No active series found.</p>
        </div>
    );

    return (
        <>
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-center">
                    <h2 className="heading-primary-1 text-3xl font-extrabold">{series.name} {series.year}</h2>
                    {allSeasons.length > 1 && (
                        <div className="flex items-center gap-2">
                            <label htmlFor="season-select" className="text-sm font-semibold text-muted">Season:</label>
                            <select
                                id="season-select"
                                value={selectedSeason}
                                onChange={(e) => setSelectedSeason(e.target.value)}
                                className="select-field p-2"
                            >
                                {allSeasons.map(season => (
                                    <option key={season.id} value={season.id}>{season.name} - {season.year}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatTile label="Total Entries" value={series.summary?.totalEntries ?? 0} />
                    <StatTile label="Unique Players" value={series.summary?.uniquePlayers ?? 0} role="info" />
                    <StatTile label="Points Awarded" value={series.summary?.pointsAwarded ?? 0} />
                    <StatTile label="Nightly Payouts" value={`$${series.summary?.nightlyPayouts ?? 0}`} />
                    <StatTile label="Elim Brackets" value={series.summary?.totals?.deWeeks ?? 0} />
                    <StatTile label="Round Robins" value={series.summary?.totals?.rrWeeks ?? 0} />
                </div>

                <div className="overflow-x-auto card rounded-lg shadow-md">
                    <table className="w-full table-fixed divide-y divide-low">
                        <thead className="table-header">
                            <tr>
                                <th className="w-[10%] px-2 sm:px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Rank</th>
                                <th className="w-[40%] px-2 sm:px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Player</th>
                                <th className="w-[15%] px-2 sm:px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Points</th>
                                <th className="w-[15%] px-2 sm:px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">High Out</th>
                                <th className="w-[20%] px-2 sm:px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Events</th>
                            </tr>
                        </thead>
                        <tbody className="surface divide-y divide-low">
                            {players.map(p => {
                                const isHighest = (series.summary?.totals?.overallHighestOut || 0) > 0 && (p.highestOut || 0) === (series.summary?.totals?.overallHighestOut || 0);
                                return (
                                    <tr key={`${p.name}-${p.rank}`} className="row-alt cursor-pointer hover:opacity-80" onClick={() => setSelectedPlayer(p)}>
                                        <td className="px-2 sm:px-4 py-3 text-center text-sm font-semibold text-strong">{p.rank}</td>
                                        <td className="px-2 sm:px-4 py-3 text-left text-sm text">
                                            <div className="flex flex-col items-start gap-1">
                                                <span className="font-medium">{p.name}</span>
                                                <div className="flex flex-wrap gap-1">
                                                    {p.isFinalist && <Tag label="Finalist" role="success" />}
                                                    {p.hasRaffle6Plus && <Tag label="6+" role="info" />}
                                                    {p.hasRaffle8Plus && <Tag label="8+" role="warning" />}
                                                    {p.hasRaffle10Plus && <Tag label="10+" role="warning" />}
                                                    {isHighest && <Tag label="High Out" role="primary" />}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-2 sm:px-4 py-3 text-center text-sm text">{p.totalPoints ?? 0}</td>
                                        <td className="px-2 sm:px-4 py-3 text-center text-sm text">{(p.highestOut || 0) === 0 ? 'x' : p.highestOut}</td>
                                        <td className="px-2 sm:px-4 py-3 text-center text-sm text">{p.totalWeeks ?? 0}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mystery Out Section */}
                <div className="mt-8">
                    <h2 className="heading-primary-1 text-2xl font-extrabold text-center mb-4">Mystery Out Results</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <StatTile label="Number of Wins" value={mysteryOutWins} />
                        <StatTile label="Total Payouts" value={`$${mysteryOutTotalPayout}`} role="success" />
                        <StatTile label="Current Pot" value={`$${currentPotDisplay}`} role="info" />
                    </div>

                    <div className="overflow-x-auto card rounded-lg shadow-md">
                        <table className="w-full table-fixed divide-y divide-low">
                            <thead className="table-header">
                                <tr>
                                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Week</th>
                                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Mystery Out</th>
                                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">Total</th>
                                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Player</th>
                                </tr>
                            </thead>
                            <tbody className="surface divide-y divide-low">
                                {series?.mysteryOut?.sort((a,b) => (a.week || 0) - (b.week || 0)).map((entry, idx) => (
                                    <tr key={entry.id || idx} className="row-alt">
                                        <td className="px-2 sm:px-4 py-3 text-center text-sm">{entry.week}</td>
                                        <td className="px-2 sm:px-4 py-3 text-center text-sm">{entry.mysteryOut}</td>
                                        <td className="px-2 sm:px-4 py-3 text-center text-sm">${entry.total}</td>
                                        <td className="px-2 sm:px-4 py-3 text-left text-sm">{entry.player}</td>
                                    </tr>
                                ))}
                                {(!series?.mysteryOut || series.mysteryOut.length === 0) && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4 text-muted italic">No Mystery Out data for this season.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {selectedPlayer && <PlayerStatsModal player={selectedPlayer} series={series} onClose={() => setSelectedPlayer(null)} />}
        </>
    );
};